import { useMemo, useState } from 'react';
import { ArrowUpDown } from 'lucide-react';

import { usePatientPriorAuthsQuery, type PriorAuthSummaryT } from '@/entities/prior-auth';
import { compareTableValues, toTableCellText, type SortDirection } from '@/shared/lib/table';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/shadcn/alert';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/shadcn/table';

export interface PriorAuthsTableProps {
  userId: string;
  /** Invoked when the clinician clicks a row. */
  onSelect: (summary: PriorAuthSummaryT) => void;
}

type SortKey = keyof PriorAuthSummaryT;

const SORTABLE_COLUMNS: readonly SortKey[] = [
  'status',
  'currentStatus',
  'currentStatusDate',
  'nextResponseDeadline',
];

/**
 * Thin table view over ``PriorAuthSummaryT`` rows.
 *
 * Sorting only runs against scalar fields we own — status, dates, and the
 * authorization number. We intentionally skip the ``isExpedited`` /
 * ``attachmentRequired`` flags because they're surfaced as badges, not
 * sort targets.
 */
export function PriorAuthsTable({ userId, onSelect }: PriorAuthsTableProps) {
  const query = usePatientPriorAuthsQuery(userId);
  const [sortColumn, setSortColumn] = useState<SortKey>('currentStatusDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const sortedRows = useMemo(() => {
    const rows = query.data ?? [];
    const next = [...rows];
    next.sort((a, b) => {
      const result = compareTableValues(a[sortColumn], b[sortColumn]);
      return sortDirection === 'asc' ? result : -result;
    });
    return next;
  }, [query.data, sortColumn, sortDirection]);

  if (query.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading prior authorizations…</div>;
  }

  if (query.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load prior authorizations</AlertTitle>
        <AlertDescription>
          {query.error instanceof Error ? query.error.message : 'An unknown error occurred.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (sortedRows.length === 0) {
    return <div className="text-sm text-muted-foreground">No prior authorizations found.</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableHeader
            label="Status"
            field="status"
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onClick={(field) => handleSortClick(field, sortColumn, setSortColumn, setSortDirection)}
          />
          <TableHead>DoseSpot status</TableHead>
          <TableHead>Case #</TableHead>
          <SortableHeader
            label="Deadline"
            field="nextResponseDeadline"
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onClick={(field) => handleSortClick(field, sortColumn, setSortColumn, setSortDirection)}
          />
          <SortableHeader
            label="Updated"
            field="currentStatusDate"
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onClick={(field) => handleSortClick(field, sortColumn, setSortColumn, setSortDirection)}
          />
          <TableHead>Auth #</TableHead>
          <TableHead>Flags</TableHead>
        </TableRow>
      </TableHeader>

      <TableBody>
        {sortedRows.map((row) => (
          <TableRow
            key={row.id}
            className="cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={() => onSelect(row)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              onSelect(row);
            }}
          >
            <TableCell>
              <Badge variant="secondary" className="rounded-full">
                {toTableCellText(row.status)}
              </Badge>
            </TableCell>
            <TableCell>{toTableCellText(row.currentStatus)}</TableCell>
            <TableCell>{toTableCellText(row.dosespotPriorAuthCaseNumber ?? null)}</TableCell>
            <TableCell>{toTableCellText(row.nextResponseDeadline ?? null)}</TableCell>
            <TableCell>{toTableCellText(row.currentStatusDate)}</TableCell>
            <TableCell>{toTableCellText(row.authorizationNumber ?? null)}</TableCell>
            <TableCell>
              <div className="flex flex-wrap gap-1">
                {row.isExpedited ? (
                  <Badge variant="outline" className="text-[10px]">
                    Expedited
                  </Badge>
                ) : null}
                {row.attachmentRequired ? (
                  <Badge variant="outline" className="text-[10px]">
                    Attachment Required
                  </Badge>
                ) : null}
                {row.isOffline ? (
                  <Badge variant="outline" className="text-[10px]">
                    Offline
                  </Badge>
                ) : null}
                {row.canAppeal ? (
                  <Badge variant="outline" className="text-[10px]">
                    Appealable
                  </Badge>
                ) : null}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface SortableHeaderProps {
  label: string;
  field: SortKey;
  sortColumn: SortKey;
  sortDirection: SortDirection;
  onClick: (field: SortKey) => void;
}

function SortableHeader({ label, field, sortColumn, sortDirection, onClick }: SortableHeaderProps) {
  if (!SORTABLE_COLUMNS.includes(field)) {
    return <TableHead>{label}</TableHead>;
  }
  const isActive = sortColumn === field;
  const indicator = isActive ? (sortDirection === 'asc' ? '↑' : '↓') : null;

  return (
    <TableHead>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="-ml-2 h-8 px-2 font-medium"
        onClick={() => onClick(field)}
      >
        {label}
        {indicator ? <span className="ml-1 text-xs">{indicator}</span> : null}
        <ArrowUpDown className="ml-2 h-4 w-4" aria-hidden="true" />
      </Button>
    </TableHead>
  );
}

function handleSortClick(
  field: SortKey,
  sortColumn: SortKey,
  setSortColumn: (f: SortKey) => void,
  setSortDirection: (updater: (prev: SortDirection) => SortDirection) => void
) {
  if (sortColumn === field) {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    return;
  }
  setSortColumn(field);
  setSortDirection(() => 'asc');
}
